import React, { useEffect, useState } from 'react';
import {
  Store, Search, Trash2, MapPin, X,
  Grid3x3, List, Table as TableIcon, Map as MapIcon
} from 'lucide-react';
import api from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAlert } from '../../components/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { AddButton } from '@/components/ui/AddButton';

interface SalesPoint {
  id: number;
  client_id: number;
  name: string;
  code: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  maps_link: string | null;
  phone: string | null;
  email: string | null;
  manager_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  notes: string | null;
  recommended_time: string | null;
}

const BuyerSalesPointsTailwind: React.FC = () => {
  const { showConfirm } = useConfirm();
  const { showSuccess, showError } = useAlert();
  const { user } = useAuth();

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed` : '';
  };

  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'grid' : 'table'
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editing, setEditing] = useState<SalesPoint | null>(null);
  const [showMapPreview, setShowMapPreview] = useState<boolean>(false);
  const [listMapPreviewPointId, setListMapPreviewPointId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    phone: '',
    email: '',
    manager_name: '',
    is_active: true,
    notes: '',
  });

  useEffect(() => {
    void load();
  }, []);

  const load = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await api.get('/clients/me/sales-points');
      setSalesPoints(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setBusy(false);
    }
  };


  const openModal = (item?: SalesPoint): void => {
    if (item) {
      setEditing(item);
      setForm({
        name: item.name,
        code: item.code || '',
        address: item.address || '',
        city: item.city || '',
        postal_code: item.postal_code || '',
        country: item.country || 'France',
        maps_link: item.maps_link || '',
        phone: item.phone || '',
        email: item.email || '',
        manager_name: item.manager_name || '',
        is_active: item.is_active,
        notes: item.notes || '',
      });
    } else {
      setEditing(null);
      setForm({
        name: '',
        code: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        maps_link: '',
        phone: '',
        email: user?.role === 'client' ? (user?.email || '') : '',
        manager_name: '',
        is_active: true,
        notes: '',
      });
    }
    setShowMapPreview(false);
    setShowModal(true);
  };

  const save = async (): Promise<void> => {
    try {
      const addressComplete = buildAddressComplete(form.address || '', form.postal_code || '', form.city || '');
      const payload = {
        ...form,
        maps_link: buildMapsLink(addressComplete),
      };
      if (editing) {
        await api.patch(`/clients/me/sales-points/${editing.id}`, payload);
        showSuccess(`Point de vente "${form.name}" modifié avec succès`);
      } else {
        await api.post('/clients/me/sales-points', payload);
        showSuccess(`Point de vente "${form.name}" créé avec succès`);
      }
      setShowModal(false);
      setShowMapPreview(false);
      await load();
    } catch (e: any) {
      console.error('Save error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const del = async (item: SalesPoint): Promise<void> => {
    const isLastSalesPoint = salesPoints.length === 1;

    const confirmed = await showConfirm({
      title: isLastSalesPoint ? '⚠️ Attention : Dernier point de vente' : 'Supprimer le point de vente',
      message: isLastSalesPoint
        ? `Vous êtes sur le point de supprimer votre dernier point de vente "${item.name}". Vous n'aurez plus aucun point de vente après cette suppression. Êtes-vous sûr de vouloir continuer ?`
        : `Êtes-vous sûr de vouloir supprimer le point de vente "${item.name}" ? Cette action est irréversible.`,
      confirmText: isLastSalesPoint ? 'Supprimer quand même' : 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/clients/me/sales-points/${item.id}`);
      showSuccess(`Point de vente "${item.name}" supprimé avec succès`);
      await load();
    } catch (e: any) {
      console.error('Delete error:', e);
      const errorDetail = e.response?.data?.detail || '';

      // Check if error is about linked orders
      if (errorDetail.includes('commande(s)')) {
        const forceConfirmed = await showConfirm({
          title: '⚠️ Commandes liées détectées',
          message: `${errorDetail} Les commandes seront détachées de ce point de vente mais ne seront pas supprimées.`,
          confirmText: 'Confirmer la suppression',
          cancelText: 'Annuler',
          severity: 'error'
        });

        if (forceConfirmed) {
          try {
            await api.delete(`/clients/me/sales-points/${item.id}?force=true`);
            showSuccess(`Point de vente "${item.name}" supprimé avec succès`);
            await load();
          } catch (e2: any) {
            console.error('Force delete error:', e2);
            showError(e2.response?.data?.detail || 'Erreur lors de la suppression du point de vente');
          }
        }
      } else {
        showError(errorDetail || 'Erreur lors de la suppression du point de vente');
      }
    }
  };

  const filtered = salesPoints.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      item.name.toLowerCase().includes(s) ||
      item.code?.toLowerCase().includes(s) ||
      item.city?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-cyan-50/20 to-slate-50 dark:from-slate-950 dark:via-cyan-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-3 py-3 sm:px-6 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Store className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Mes Points de Vente</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {salesPoints.length} magasin{salesPoints.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <AddButton
          onClick={() => openModal()}
          label="Nouveau"
          className="w-auto"
        />
      </div>

      {/* Search and View Switcher */}
      <div className="px-3 py-3 sm:px-6 sm:py-4 flex flex-row gap-2 sm:gap-4 justify-between items-center">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un point de vente..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1 bg-white dark:bg-slate-800">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'grid'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Grille"
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'list'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Liste"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`hidden md:block p-2 rounded-md transition-colors ${viewMode === 'table'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Tableau"
          >
            <TableIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 px-3 pb-3 sm:px-6 sm:pb-6 min-h-0 ${viewMode === 'table' ? 'overflow-hidden' : 'overflow-auto'}`}>
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <Store className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucun point de vente</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => openModal(item)}
                className="cursor-pointer p-3 sm:p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 hover:scale-105 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-4"
                style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center">
                      <Store className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-slate-100">{item.name}</h3>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); del(item); }}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                      aria-label="Supprimer"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3 mb-4 px-1">
                  {/* Address */}
                  <div className="flex items-start gap-3 text-sm">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className={`h-3.5 w-3.5 ${buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`line-clamp-2 mt-1 ${item.address ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                        {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') || 'Adresse non renseignée'}
                      </span>
                      {item.country && (
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">{item.country}</div>
                      )}

                      {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') && (
                        <div className="mt-2">
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setListMapPreviewPointId((curr) => (curr === item.id ? null : item.id));
                              }}
                              className={
                                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                (listMapPreviewPointId === item.id
                                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                              }
                            >
                              <MapIcon className="h-3.5 w-3.5" />
                              Carte
                            </button>
                            <a
                              href={buildMapsLink(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Ouvrir dans Google Maps
                            </a>
                          </div>

                          {listMapPreviewPointId === item.id && (
                            <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                              <iframe
                                title="Google Maps"
                                src={buildMapsEmbedSrc(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                className="w-full h-64"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.is_active
                    ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                    {item.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2 sm:space-y-3">
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => openModal(item)}
                className="p-3 sm:p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4 cursor-pointer"
                style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
              >
                  {/* Top row: icon + name + status + delete */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Store className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate">{item.name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mt-0.5 ${item.is_active
                      ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}>
                      {item.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); del(item); }}
                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex-shrink-0"
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Address details */}
                <div className="flex items-start gap-2 text-sm mt-2 pl-1">
                  <MapPin className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                  <div className="flex-1 min-w-0">
                    <span className={`block truncate ${item.address ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                      {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') || 'Adresse non renseignée'}
                    </span>
                    {item.country && (
                      <div className="text-xs text-slate-500 dark:text-slate-500">{item.country}</div>
                    )}

                    {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setListMapPreviewPointId((curr) => (curr === item.id ? null : item.id));
                          }}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                            (listMapPreviewPointId === item.id
                              ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                          }
                        >
                          <MapIcon className="h-3.5 w-3.5" />
                          Carte
                        </button>
                        <a
                          href={buildMapsLink(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Google Maps
                        </a>
                      </div>
                    )}

                    {listMapPreviewPointId === item.id && (
                      <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        <iframe
                          title="Google Maps"
                          src={buildMapsEmbedSrc(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                          className="w-full h-48 sm:h-64"
                          referrerPolicy="no-referrer-when-downgrade"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'table' ? (
          <>
            {/* Mobile: stacked cards (< md) */}
            <div className="md:hidden space-y-2">
              {filtered.map((item, idx) => (
                <div
                  key={item.id}
                  onClick={() => openModal(item)}
                  className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 cursor-pointer hover:border-cyan-300 dark:hover:border-cyan-700 transition-all animate-in fade-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Store className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{item.name}</div>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${item.is_active
                        ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                        {item.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); del(item); }}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex-shrink-0"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') && (
                    <div className="mt-2 pl-1 flex items-start gap-2 text-sm">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate text-slate-600 dark:text-slate-400">
                          {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '')}
                        </span>
                        {item.country && <span className="text-xs text-slate-500">{item.country}</span>}
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setListMapPreviewPointId((curr) => (curr === item.id ? null : item.id));
                            }}
                            className={
                              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                              (listMapPreviewPointId === item.id
                                ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                            }
                          >
                            <MapIcon className="h-3.5 w-3.5" />
                            Carte
                          </button>
                          <a
                            href={buildMapsLink(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Google Maps
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  {item.recommended_time && (
                    <div className="mt-2 pl-1 text-xs text-slate-500 dark:text-slate-400">
                      Horaire : {item.recommended_time}
                    </div>
                  )}
                  {listMapPreviewPointId === item.id && (
                    <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <iframe
                        title="Google Maps"
                        src={buildMapsEmbedSrc(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                        className="w-full h-48"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: real table (md+) */}
            <div className="hidden md:block bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-950/30 border-b-2 border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Adresse</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Horaire recommandé</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Statut</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filtered.map((item, idx) => (
                    <tr
                      key={item.id}
                      onClick={() => openModal(item)}
                      className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
                      style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Store className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 dark:text-slate-100">{item.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs">
                        <div className="truncate">{buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') || '—'}</div>
                        {item.country && <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">{item.country}</div>}

                        {buildAddressComplete(item.address || '', item.postal_code || '', item.city || '') && (
                          <div className="mt-2">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setListMapPreviewPointId((curr) => (curr === item.id ? null : item.id));
                                }}
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                  (listMapPreviewPointId === item.id
                                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                }
                              >
                                <MapIcon className="h-3.5 w-3.5" />
                                Carte
                              </button>
                              <a
                                href={buildMapsLink(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Ouvrir dans Google Maps
                              </a>
                            </div>

                            {listMapPreviewPointId === item.id && (
                              <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                <iframe
                                  title="Google Maps"
                                  src={buildMapsEmbedSrc(buildAddressComplete(item.address || '', item.postal_code || '', item.city || ''))}
                                  className="w-full h-64"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {item.recommended_time || <span className="italic text-slate-400 dark:text-slate-500">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.is_active
                          ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                          {item.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); del(item); }}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-950/50 rounded-lg transition-all duration-200"
                          aria-label="Supprimer"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Store className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{editing ? 'Modifier' : 'Nouveau'} Point de Vente</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Gérez vos magasins physiques</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowMapPreview(false);
                  setShowModal(false);
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                aria-label="Fermer"
                title="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="client_sales_point_name" className="block text-sm font-medium mb-1">Nom du point de vente *</label>
                  <input
                    id="client_sales_point_name"
                    type="text"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>

                {/* GPS Address Picker */}
                <div className="md:col-span-2 border-2 border-cyan-300 p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/20 my-4">
                  <label className="block text-sm font-medium mb-3 text-cyan-700 dark:text-cyan-300">

                    <MapPin className="inline h-4 w-4 mr-1" />
                    Localisation
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="client_sales_point_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                      <input
                        id="client_sales_point_address"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.address}
                        onChange={(e) =>
                          setForm((prev) => {
                            const next = { ...prev, address: e.target.value };
                            const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                            return { ...next, maps_link: buildMapsLink(addressComplete) };
                          })
                        }
                        placeholder="Ex: 44 Avenue Gambetta"
                      />
                    </div>

                    <div>
                      <label htmlFor="client_sales_point_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                      <input
                        id="client_sales_point_postal_code"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.postal_code}
                        onChange={(e) =>
                          setForm((prev) => {
                            const next = { ...prev, postal_code: e.target.value };
                            const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                            return { ...next, maps_link: buildMapsLink(addressComplete) };
                          })
                        }
                        placeholder="Ex: 17100"
                      />
                    </div>

                    <div>
                      <label htmlFor="client_sales_point_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                      <input
                        id="client_sales_point_city"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                        value={form.city}
                        onChange={(e) =>
                          setForm((prev) => {
                            const next = { ...prev, city: e.target.value };
                            const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '');
                            return { ...next, maps_link: buildMapsLink(addressComplete) };
                          })
                        }
                        placeholder="Ex: Saintes"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="client_sales_point_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                      <input
                        id="client_sales_point_address_complete"
                        className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600"
                        value={buildAddressComplete(form.address, form.postal_code, form.city)}
                        readOnly
                      />

                      {buildAddressComplete(form.address, form.postal_code, form.city) && (
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setShowMapPreview((v) => !v)}
                            className={
                              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                              (showMapPreview
                                ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                            }
                          >
                            <MapIcon className="h-3.5 w-3.5" />
                            Carte
                          </button>
                          {form.maps_link && (
                            <a
                              href={form.maps_link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Ouvrir dans Google Maps
                            </a>
                          )}
                        </div>
                      )}

                      {showMapPreview && (
                        <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                          <iframe
                            title="Google Maps"
                            src={buildMapsEmbedSrc(buildAddressComplete(form.address, form.postal_code, form.city))}
                            className="w-full h-64"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="client_sales_point_country" className="block text-sm font-medium mb-1">Pays</label>
                  <input
                    id="client_sales_point_country"
                    type="text"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="client_sales_point_notes" className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    id="client_sales_point_notes"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 rounded"
                    />
                    <span className="text-sm font-medium">Point de vente actif</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Annuler</button>
                <button onClick={save} className="px-4 py-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-colors font-medium">Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerSalesPointsTailwind;
